import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

// tools & utils
import { stringify } from 'qs';
import { getToken } from 'utils';
import { getBaseUrl } from 'utils/helper';

// redux
import { logout, setGlobalError } from '../auth';

const baseUrl = getBaseUrl();

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }

    return headers;
  },
  paramsSerializer: (params) => {
    return stringify(params);
  }
});

export const baseQueryWithAuthErrorHandling: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    const status = result.error.status;

    // 🔴 Network / connection error
    if (status === 'FETCH_ERROR') {
      api.dispatch(setGlobalError(true));
    }

    if (status === 401) {
      api.dispatch(logout());
    }
  }

  return result;
};

// Same as baseQueryWithAuthErrorHandling but without the global error screen on
// FETCH_ERROR. Used for the long-running (LLM) custom-prompt calls: they abort
// the request themselves on a client timeout, which surfaces as FETCH_ERROR and
// must not take the whole app down.
export const baseQueryWithoutGlobalError: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    api.dispatch(logout());
  }

  return result;
};

export const serverApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuthErrorHandling,
  tagTypes: [
    'Company',
    'Gateway',
    'GatewayApp',
    'Property',
    'Sensor',
    'SensorIssue',
    'Building',
    'BuildingInstaller',
    'BuildingSync',
    'BuildingShipping',
    'BuildingShippingDelivery',
    'BuildingShippingOrdersInfo',
    'BuildingShippingContactInfo',
    'ParsedSubmission',
    'Notification',
    'CompanyApplianceThreshold',
    'Unit',
    'Appliance',
    'User',
    'UserRole',
    'SensorConfigDefault',
    'NotificationContact',
    'SensorDownlinkConfigParams',
    'SensorGallonsThreshold',
    'Event',
    'ExternalAccount',
    'RealpageCompany',
    'RealpageSubscription',
    'CityBill',
    'UtilityCompany',
    'PropertyOwner',
    'CustomPrompt',
    'CustomPromptVersion',
    'CustomPromptSession'
  ],
  endpoints: () => ({})
});
